import React from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Box, Button, CircularProgress, Grid, Switch, Typography } from "@mui/material";
import { Add, FilterList, Visibility } from "@mui/icons-material";

import { BudgetCard, Drawer, DrawerControl, MetaDrivenTable, PaginationControls, SearchField } from "../../../shared/components";
import { RaiseQuery, QueryDetails, QueryFilters, headers, metrics, useQueryAndRFI } from "../index";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { QueryHeader } from "../components/QueryDetails";

export const Queries: React.FC = () => {

    const { isAllowedToRaisequery } = useCapabilities();

    const { projectId } = useParams<{ projectId: string }>();

    const [searchParams, setSearchParams] = useSearchParams();

    const selected = searchParams.get('selected');
    const id = searchParams.get('id');

    const openDrawer = (riskId: string) => {
        const params = new URLSearchParams(searchParams);
        params.set("selected", "true");
        params.set("id", riskId);
        setSearchParams(params, { replace: true });
    };

    const closeDrawer = () => {
        const params = new URLSearchParams(searchParams);
        params.delete("selected");
        params.delete("id");
        setSearchParams(params, { replace: true });
    };

    const {
        isFetching, isLoading, data, stateData, searchDraft, pagination, raiseQuery, phases, filterDrawer, filter, filterTemp, assignedToMe,
        handleAssignedToMeChange, setFilterDrawer, setRaiseQuery, handleChangeSearch, handleClear, handleSearch,
        changePage, changeRowsPerPage, onClose, handleUpdateFilter, onClear, onApply, onResolved, onEdit, refetch, refetchStates
    } = useQueryAndRFI(projectId);

    if (isLoading || isFetching) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box py={2} display={'flex'} gap={2} flexDirection={'column'} height={'100%'} overflow={'hidden'}>

            <Grid container spacing={2} px={2}>
                {
                    [
                        { label: 'Total Queries', value: stateData.total_queries, icon: 'Balance', color: 'info' as const },
                        { label: 'Open Queries', value: stateData.open_queries, icon: 'Cost', color: 'amber' as const },
                        { label: 'Pending Response', value: stateData.pending_response, icon: 'Assignment', color: 'amber' as const },
                        { label: 'Overdue', value: stateData.overdue_queries, icon: 'Trending', color: 'red' as const },
                    ].map((value, index) => (
                        <Grid size={3} key={index}>
                            <BudgetCard label={value.label} value={value.value} icon={value.icon} color={value.color} removeMoney={true} />
                        </Grid>
                    ))
                }
            </Grid>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} px={2}>
                <Box mr={'auto'}>
                    <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="subtitle2">Assigned to Me: </Typography>
                        <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                    </Box>
                    <Button
                        startIcon={<FilterList />}
                        variant="text"
                        color="inherit"
                        onClick={() => { setFilterDrawer(true) }}
                    >
                        FILTERS
                    </Button>
                    {
                        isAllowedToRaisequery && (
                            <Button
                                startIcon={<Add />}
                                variant="text"
                                color="primary"
                                onClick={() => {
                                    setRaiseQuery({ open: true, status: false, id: null })
                                }}
                            >
                                RAISE QUERY
                            </Button>
                        )
                    }
                </Box>
            </Box>

            <MetaDrivenTable headers={headers} data={data} dataMetrics={metrics} sortDisabled={true} actions={[
                {
                    label: <Visibility />,
                    handleClick: (row: any) => {
                        openDrawer(row.id);
                    },
                }]}
            />

            <Box flexShrink={0}>
                <PaginationControls
                    total={pagination.total}
                    rowsPerPage={pagination.limit}
                    currentPage={pagination.page}
                    handlePageChange={changePage}
                    handleRowsPerPage={changeRowsPerPage}
                />
            </Box>

            <Drawer
                isOpen={!!selected}
                header={
                    <QueryHeader
                        queryId={String(id)}
                        closeDrawer={closeDrawer}
                    />
                }
                children={
                    <QueryDetails
                        queryId={String(id)}
                        onResolved={() => onResolved(String(id))}
                        onEdit={() => onEdit(String(id))}
                    />
                }
            />

            <DrawerControl
                title={"Raise Query"}
                subTitle={""}
                isOpen={raiseQuery.open}
                handleClose={onClose}
                children={
                    <RaiseQuery
                        id={raiseQuery.id}
                        projectId={projectId}
                        editStatus={raiseQuery.status}
                        onSaved={() => {
                            onClose();
                            closeDrawer();
                            refetch();
                            refetchStates();
                        }}
                        onClose={onClose}
                    />
                }
            />

            <DrawerControl
                title={"Filter Queries"}
                subTitle={""}
                isOpen={filterDrawer}
                handleClose={() => { setFilterDrawer(false) }}
                children={
                    <QueryFilters
                        filter={filterTemp}
                        handleUpdate={handleUpdateFilter}
                        isPendingPhase={false}
                        phaseOptions={phases?.data?.data || []}
                        isProjectsearchDisabled={!!projectId}
                        hiddenFilters={projectId ? [] : ['portfolio', 'programme']}
                    />
                }
                filterActions={[
                    {
                        label: 'Clear',
                        variant: 'outlined',
                        handleClick: () => onClear(true)
                    },
                    {
                        label: 'Apply',
                        variant: 'contained',
                        handleClick: onApply,
                    }
                ]}
            />

        </Box>
    );

};